import React, { useEffect, useRef, useState } from 'react';
import './FileViewer.css';

const OFFICE_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];

function getExtension(filename = '') {
  const idx = String(filename).lastIndexOf('.');
  return idx >= 0 ? String(filename).slice(idx).toLowerCase() : '';
}

export function isOfficeFile(file = {}) {
  const ext = getExtension(file.original_filename);
  if (OFFICE_EXTENSIONS.includes(ext)) return true;
  const mime = (file.content_type || '').toLowerCase();
  return mime.includes('msword') || mime.includes('spreadsheet') || mime.includes('presentation') || mime.includes('opendocument');
}

export function isPdfFile(file = {}) {
  return getExtension(file.original_filename) === '.pdf' || file.content_type === 'application/pdf';
}

export function needsFillPreview(file = {}) {
  return isOfficeFile(file) || isPdfFile(file);
}

function FileVideoPlayer({ src, className }) {
  const videoRef = useRef(null);
  const [buffering, setBuffering] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const showBuffering = () => setBuffering(true);
    const hideBuffering = () => setBuffering(false);

    video.addEventListener('play', showBuffering);
    video.addEventListener('waiting', showBuffering);
    video.addEventListener('seeking', showBuffering);
    video.addEventListener('playing', hideBuffering);
    video.addEventListener('canplaythrough', hideBuffering);
    video.addEventListener('error', hideBuffering);
    video.addEventListener('ended', hideBuffering);

    return () => {
      video.removeEventListener('play', showBuffering);
      video.removeEventListener('waiting', showBuffering);
      video.removeEventListener('seeking', showBuffering);
      video.removeEventListener('playing', hideBuffering);
      video.removeEventListener('canplaythrough', hideBuffering);
      video.removeEventListener('error', hideBuffering);
      video.removeEventListener('ended', hideBuffering);
    };
  }, [src]);

  return (
    <div className="file-viewer-video-wrap">
      <video ref={videoRef} src={src} controls className={className} preload="metadata" />
      {buffering && (
        <div className="file-viewer-video-loading" aria-live="polite" aria-busy="true">
          <div className="file-viewer-video-spinner" />
        </div>
      )}
    </div>
  );
}

const FileViewer = ({ file, downloadUrl, fill = false }) => {
  if (!file || !downloadUrl) {
    return <div className="file-viewer-empty">Просмотр недоступен</div>;
  }

  const category = file.file_category;
  const isPdf = isPdfFile(file);
  const rootClass = `file-viewer-root ${fill ? 'file-viewer-root--fill' : ''}`;
  const iframeClass = `file-viewer-iframe ${fill ? 'file-viewer-iframe--fill' : ''}`;

  if (category === 'image') {
    return (
      <div className={rootClass}>
        <img src={downloadUrl} alt={file.original_filename} className="file-viewer-image" />
      </div>
    );
  }
  if (category === 'video') {
    return (
      <div className={rootClass}>
        <FileVideoPlayer src={downloadUrl} className="file-viewer-video" />
      </div>
    );
  }
  if (category === 'audio') {
    return (
      <div className={rootClass}>
        <audio src={downloadUrl} controls className="file-viewer-audio" />
      </div>
    );
  }
  if (isPdf) {
    return (
      <div className={rootClass}>
        <iframe title={file.original_filename} src={downloadUrl} className={iframeClass} />
      </div>
    );
  }
  if (category === 'document' && isOfficeFile(file)) {
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadUrl)}`;
    return (
      <div className={rootClass}>
        <iframe title={file.original_filename} src={officeUrl} className={iframeClass} allowFullScreen />
      </div>
    );
  }
  if (category === 'document' && (file.content_type || '').startsWith('text/')) {
    return (
      <div className={rootClass}>
        <iframe title={file.original_filename} src={downloadUrl} className={iframeClass} />
      </div>
    );
  }

  return (
    <div className="file-viewer-fallback">
      <p>Предпросмотр для этого типа файла недоступен.</p>
      <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline">
        Скачать файл
      </a>
    </div>
  );
};

export default FileViewer;
